import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { BASE_URL } from "../../../../data/BaseUrl";
// Gym data fetched directly from /gym?status=active (not Redux) to ensure ALL active gyms are included
import "../../../../styles/trainer-report/new-gyms.css";

const TRAINER_TARGET = 4;
const RECEP_TARGET = 2;

// ── Helpers ──────────────────────────────────────────────────────────────────

const getStatus = (approvedT, approvedR, pendingT, pendingR) => {
  if (approvedT === 0 && approvedR === 0) return "critical";
  const projectedT = approvedT + pendingT;
  const projectedR = approvedR + pendingR;
  if (projectedT >= TRAINER_TARGET && projectedR >= RECEP_TARGET) return "almost_ready";
  if (pendingT > 0 || pendingR > 0) return "hiring";
  return "critical";
};

const getProgressPct = (approvedT, approvedR) => {
  const maxT = TRAINER_TARGET;
  const maxR = RECEP_TARGET;
  const total = maxT + maxR;
  const have = Math.min(approvedT, maxT) + Math.min(approvedR, maxR);
  return Math.round((have / total) * 100);
};

const getProgressColor = (pct, status) => {
  if (status === "critical") return "red";
  if (pct >= 100) return "green";
  if (status === "almost_ready") return "blue";
  return "yellow";
};

const STATUS_LABELS = {
  critical:     "Critical",
  hiring:       "Hiring",
  almost_ready: "Almost Ready",
  ready:        "Ready",
};

// ── KPI Flag Cards ────────────────────────────────────────────────────────────
const FlagCards = ({ kpi, activeFilter, onFilter }) => {
  const cards = [
    {
      key:   "critical",
      label: "Zero Staff",
      value: kpi.critical,
      sub:   "gyms with 0 approved staff",
      mod:   kpi.critical > 0 ? "red" : "grey",
      clickable: true,
    },
    {
      key:   "hiring",
      label: "Hiring In Progress",
      value: kpi.hiring,
      sub:   "pending staff, gaps remain",
      mod:   kpi.hiring > 0 ? "yellow" : "grey",
      clickable: true,
    },
    {
      key:   "almost_ready",
      label: "Almost Ready",
      value: kpi.almostReady,
      sub:   "approved + pending >= target",
      mod:   kpi.almostReady > 0 ? "blue" : "grey",
      clickable: true,
    },
    {
      key:   "open_positions",
      label: "Open Positions",
      value: kpi.openPositions,
      sub:   "total trainer + sales gaps",
      mod:   "grey",
      clickable: false,
    },
  ];

  return (
    <div className="ngt-flags">
      {cards.map((c) => (
        <div
          key={c.key}
          className={[
            "ngt-flag",
            `ngt-flag--${c.mod}`,
            c.clickable ? "ngt-flag--clickable" : "",
            activeFilter === c.key ? "ngt-flag--active" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => c.clickable && onFilter(c.key)}
          role={c.clickable ? "button" : undefined}
          tabIndex={c.clickable ? 0 : undefined}
          onKeyDown={
            c.clickable
              ? (e) => e.key === "Enter" && onFilter(c.key)
              : undefined
          }
          title={c.clickable ? "Click to filter" : undefined}
        >
          <div className="ngt-flag__label">{c.label}</div>
          <div className="ngt-flag__value">{c.value}</div>
          <div className="ngt-flag__sub">{c.sub}</div>
        </div>
      ))}
    </div>
  );
};

// ── Inline Drill-Down ─────────────────────────────────────────────────────────
const GymDrillDown = ({ gym, onClose }) => {
  const { gymName, approvedTrainers, approvedReceptionists, pendingTrainers, pendingReceptionists, trainerGap, recepGap } = gym;

  const formatDate = (d) => {
    if (!d) return "—";
    return moment(d).isValid() ? moment(d).format("D MMM YYYY") : d;
  };

  return (
    <div className="ngt-drilldown-panel">
      <div className="ngt-drilldown-header">
        <div className="ngt-drilldown-title">{gymName} — Staffing Detail</div>
        <button
          type="button"
          className="ngt-drilldown-close"
          onClick={onClose}
          aria-label="Close drill-down"
        >
          ✕
        </button>
      </div>

      <div className="ngt-drilldown-grid">
        {/* Approved Trainers */}
        <div className="ngt-drilldown-section">
          <div className="ngt-drilldown-section__title">
            Approved Trainers
            <span className="ngt-drilldown-section__count">
              {approvedTrainers.length} / {TRAINER_TARGET}
            </span>
          </div>
          {approvedTrainers.length > 0 ? (
            approvedTrainers.map((s, i) => (
              <div key={s.uid || s.user_id || i} className="ngt-staff-item">
                <span className="ngt-staff-dot ngt-staff-dot--green" />
                <div className="ngt-staff-info">
                  <div className="ngt-staff-name">{s.name || "—"}</div>
                  <div className="ngt-staff-meta">
                    Joined {formatDate(s.joining_date || s.date_added)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="ngt-drilldown-empty">No approved trainers yet</div>
          )}
        </div>

        {/* Approved Receptionists */}
        <div className="ngt-drilldown-section">
          <div className="ngt-drilldown-section__title">
            Approved Sales Execs
            <span className="ngt-drilldown-section__count">
              {approvedReceptionists.length} / {RECEP_TARGET}
            </span>
          </div>
          {approvedReceptionists.length > 0 ? (
            approvedReceptionists.map((s, i) => (
              <div key={s.uid || s.user_id || i} className="ngt-staff-item">
                <span className="ngt-staff-dot ngt-staff-dot--green" />
                <div className="ngt-staff-info">
                  <div className="ngt-staff-name">{s.name || "—"}</div>
                  <div className="ngt-staff-meta">
                    Joined {formatDate(s.joining_date || s.date_added)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="ngt-drilldown-empty">No approved sales execs yet</div>
          )}
        </div>

        {/* Pending Staff */}
        {(pendingTrainers.length > 0 || pendingReceptionists.length > 0) && (
          <div className="ngt-drilldown-section">
            <div className="ngt-drilldown-section__title">
              Pending Approval
              <span className="ngt-drilldown-section__count">
                {pendingTrainers.length + pendingReceptionists.length}
              </span>
            </div>
            {[...pendingTrainers, ...pendingReceptionists].map((s, i) => (
              <div key={s.uid || s.user_id || i} className="ngt-staff-item">
                <span className="ngt-staff-dot ngt-staff-dot--yellow" />
                <div className="ngt-staff-info">
                  <div className="ngt-staff-name">{s.name || "—"}</div>
                  <div className="ngt-staff-meta">
                    {s.account_type === "trainer" ? "Trainer" : "Sales Exec"} · Applied{" "}
                    {formatDate(s.joining_date || s.date_added)}
                  </div>
                </div>
                <span className="ngt-pending-badge">Pending</span>
              </div>
            ))}
          </div>
        )}

        {/* Open Positions */}
        {(trainerGap > 0 || recepGap > 0) && (
          <div className="ngt-drilldown-section">
            <div className="ngt-drilldown-section__title">
              Open Positions
              <span className="ngt-drilldown-section__count">
                {trainerGap + recepGap}
              </span>
            </div>
            {trainerGap > 0 && (
              <div className="ngt-gap-item">
                <span className="ngt-gap-item__icon">+</span>
                {trainerGap} Trainer position{trainerGap !== 1 ? "s" : ""} needed
              </div>
            )}
            {recepGap > 0 && (
              <div className="ngt-gap-item">
                <span className="ngt-gap-item__icon">+</span>
                {recepGap} Sales Exec position{recepGap !== 1 ? "s" : ""} needed
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const NewGymsTab = ({ gymFilter }) => {
  const token = useSelector((s) => s.auth?.user?.token);

  const [allActiveGyms, setAllActiveGyms] = useState([]);
  const [approvedStaff, setApprovedStaff] = useState([]);
  const [pendingStaff, setPendingStaff]   = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [activeFilter, setActiveFilter]   = useState(null); // "critical"|"hiring"|"almost_ready"|null
  const [expandedGymId, setExpandedGymId] = useState(null);

  const drilldownRef = useRef(null);

  // Reset expanded row on gym filter change
  useEffect(() => {
    setExpandedGymId(null);
    setActiveFilter(null);
  }, [gymFilter]);

  // Fetch both approved and pending staff in parallel
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    const signal = controller.signal;

    const buildParams = (extra = {}) => {
      const p = new URLSearchParams({ status: "active", limit: 9999, page: 1, ...extra });
      if (gymFilter?.value) p.set("gym_id", gymFilter.value);
      return p.toString();
    };

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = { Authorization: `Bearer ${token}` };

        // Approved staff: approval_status=1 (or no filter, then filter client-side)
        // Pending staff:  approval_status=0
        // Fetch all active gyms + approved staff + pending staff in parallel
        const gymParams = new URLSearchParams({ status: "active", limit: 999, page: 1 });
        const [gymsRes, approvedRes, pendingRes] = await Promise.all([
          fetch(`${BASE_URL}/gym?${gymParams}`, { headers, signal }),
          fetch(`${BASE_URL}/staff?${buildParams({ approval_status: 1 })}`, { headers, signal }),
          fetch(`${BASE_URL}/staff?${buildParams({ approval_status: 0 })}`, { headers, signal }),
        ]);

        if (!gymsRes.ok) throw new Error(`Gym fetch error: ${gymsRes.status}`);
        if (!approvedRes.ok) throw new Error(`Server error: ${approvedRes.status}`);
        if (!pendingRes.ok) throw new Error(`Server error: ${pendingRes.status}`);

        const [gymsJson, approvedJson, pendingJson] = await Promise.all([
          gymsRes.json(),
          approvedRes.json(),
          pendingRes.json(),
        ]);

        const extractRows = (json) =>
          Array.isArray(json.data)    ? json.data
        : Array.isArray(json.result)  ? json.result
        : Array.isArray(json.records) ? json.records
        : Array.isArray(json)         ? json
        : [];

        setAllActiveGyms(extractRows(gymsJson));
        setApprovedStaff(extractRows(approvedJson));
        setPendingStaff(extractRows(pendingJson));
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to load staffing data. Please retry.");
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchAll();
    return () => controller.abort();
  }, [token, gymFilter, reloadTrigger]);

  /**
   * Build per-gym staffing picture.
   * Only include gyms that don't meet minimum staffing (approved < target for T or R).
   * Sort by total gap descending (worst first).
   */
  const gymRows = useMemo(() => {
    // Index approved staff by gym_id
    const approvedByGym = {};
    approvedStaff.forEach((s) => {
      const gid = String(s.gym_id || "");
      if (!gid) return;
      if (!approvedByGym[gid]) {
        approvedByGym[gid] = { gymName: s.gym_name || gid, city: s.city || s.gym_city || "", trainers: [], receptionists: [] };
      }
      if (s.account_type === "trainer")      approvedByGym[gid].trainers.push(s);
      else if (s.account_type === "receptionist") approvedByGym[gid].receptionists.push(s);
    });

    // Index pending staff by gym_id
    const pendingByGym = {};
    pendingStaff.forEach((s) => {
      const gid = String(s.gym_id || "");
      if (!gid) return;
      if (!pendingByGym[gid]) {
        pendingByGym[gid] = { trainers: [], receptionists: [] };
      }
      if (s.account_type === "trainer")      pendingByGym[gid].trainers.push(s);
      else if (s.account_type === "receptionist") pendingByGym[gid].receptionists.push(s);
    });

    // Build a Set of active gym IDs from the /gym?status=active response.
    // This is the authoritative source — only gyms present here are truly active.
    // Staff records may still reference deactivated gyms, so we use this Set
    // as a final gate to exclude any gym that is no longer active.
    const activeGymIds = new Set();
    allActiveGyms.forEach((gym) => {
      const gid = String(gym.user_id || gym.uid || "");
      if (gid) activeGymIds.add(gid);
    });

    // Include ALL active gyms — this is the key: gyms with zero staff must also appear
    allActiveGyms.forEach((gym) => {
      const gid = String(gym.user_id || gym.uid || "");
      if (!gid) return;
      if (!approvedByGym[gid]) {
        approvedByGym[gid] = {
          gymName: gym.gym_name || gid,
          city: gym.city || "",
          dateAdded: gym.date_added || gym.date_added_unix || null,
          trainers: [],
          receptionists: [],
        };
      } else {
        // Fill in city if we have it from the gym endpoint but not from staff
        if (!approvedByGym[gid].city && gym.city) {
          approvedByGym[gid].city = gym.city;
        }
        // Fix gym name if it's showing raw ID
        if (approvedByGym[gid].gymName === gid && gym.gym_name) {
          approvedByGym[gid].gymName = gym.gym_name;
        }
        // Fill in dateAdded from gym endpoint
        if (!approvedByGym[gid].dateAdded) {
          approvedByGym[gid].dateAdded = gym.date_added || gym.date_added_unix || null;
        }
      }
    });

    // Apply gymFilter if set
    const filterGymId = gymFilter?.value ? String(gymFilter.value) : null;

    // Build rows — only gyms below target.
    // The activeGymIds filter here is what prevents deactivated gyms from
    // appearing: staff records that reference an inactive gym will have added
    // it to approvedByGym above, but it will not be in activeGymIds so it
    // gets dropped before any row is emitted.
    const rows = Object.entries(approvedByGym)
      .filter(([gymId]) => activeGymIds.has(gymId))
      .filter(([gymId]) => !filterGymId || gymId === filterGymId)
      .map(([gymId, { gymName, city, dateAdded, trainers, receptionists }]) => {
        const approvedT = trainers.length;
        const approvedR = receptionists.length;
        const pendingT  = pendingByGym[gymId]?.trainers.length || 0;
        const pendingR  = pendingByGym[gymId]?.receptionists.length || 0;

        const trainerGap = Math.max(0, TRAINER_TARGET - approvedT);
        const recepGap   = Math.max(0, RECEP_TARGET   - approvedR);
        const totalGap   = trainerGap + recepGap;

        // Only include gyms that are under-staffed
        if (trainerGap === 0 && recepGap === 0) return null;

        const status = getStatus(approvedT, approvedR, pendingT, pendingR);
        const pct    = getProgressPct(approvedT, approvedR);

        // Parse date_added — could be ISO string, Unix ms, or Unix s
        let addedMoment = null;
        if (dateAdded) {
          const num = Number(dateAdded);
          if (!isNaN(num) && num > 1e12) addedMoment = moment(num);        // Unix ms
          else if (!isNaN(num) && num > 1e9) addedMoment = moment(num * 1000); // Unix s
          else addedMoment = moment(dateAdded);                              // ISO string
        }
        const gymAddedDate = addedMoment?.isValid() ? addedMoment.format("DD MMM YYYY") : "—";
        const daysSinceAdded = addedMoment?.isValid() ? moment().diff(addedMoment, "days") : null;

        return {
          gymId,
          gymName,
          city,
          gymAddedDate,
          daysSinceAdded,
          approvedTrainers:      trainers,
          approvedReceptionists: receptionists,
          pendingTrainers:       pendingByGym[gymId]?.trainers || [],
          pendingReceptionists:  pendingByGym[gymId]?.receptionists || [],
          approvedT,
          approvedR,
          pendingT,
          pendingR,
          trainerGap,
          recepGap,
          totalGap,
          status,
          pct,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.totalGap - a.totalGap);

    return rows;
  }, [approvedStaff, pendingStaff, allActiveGyms, gymFilter]);

  // KPI aggregates
  const kpi = useMemo(() => {
    let critical = 0, hiring = 0, almostReady = 0, openPositions = 0;
    gymRows.forEach((r) => {
      if (r.status === "critical")     critical++;
      else if (r.status === "hiring")  hiring++;
      else if (r.status === "almost_ready") almostReady++;
      openPositions += r.totalGap;
    });
    return { critical, hiring, almostReady, openPositions };
  }, [gymRows]);

  // Filtered rows based on active KPI card
  const filteredRows = useMemo(() => {
    if (!activeFilter) return gymRows;
    return gymRows.filter((r) => r.status === activeFilter);
  }, [gymRows, activeFilter]);

  const handleFilterToggle = useCallback((key) => {
    setActiveFilter((prev) => (prev === key ? null : key));
    setExpandedGymId(null);
  }, []);

  const handleRowClick = useCallback((gymId) => {
    setExpandedGymId((prev) => (prev === gymId ? null : gymId));
  }, []);

  // Scroll drill-down into view
  useEffect(() => {
    if (expandedGymId && drilldownRef.current) {
      drilldownRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [expandedGymId]);

  return (
    <div className="sdt-container">
      {/* KPI flag cards */}
      <FlagCards kpi={kpi} activeFilter={activeFilter} onFilter={handleFilterToggle} />

      {/* Toolbar */}
      <div className="ngt-toolbar">
        {!loading && gymRows.length > 0 && (
          <span style={{ fontSize: "0.8125rem", color: "#6b7280" }}>
            {filteredRows.length} gym{filteredRows.length !== 1 ? "s" : ""} below target
          </span>
        )}
        <button
          type="button"
          className="ngt-toolbar__reload"
          onClick={() => setReloadTrigger((n) => n + 1)}
          aria-label="Reload data"
        >
          ↻ Reload
        </button>
      </div>

      {/* Active filter banner */}
      {activeFilter && (
        <div className="ngt-filter-banner">
          Showing:{" "}
          <strong>
            {activeFilter === "critical"
              ? "Zero Staff (0 approved)"
              : activeFilter === "hiring"
              ? "Hiring In Progress"
              : "Almost Ready (pending >= target)"}
          </strong>
          <button
            type="button"
            className="ngt-filter-banner__clear"
            onClick={() => setActiveFilter(null)}
          >
            ✕ Clear
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="ngt-error">
          {error}
          <button
            type="button"
            className="ngt-error__retry"
            onClick={() => setReloadTrigger((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="ngt-table-wrap">
          <div className="ngt-loading">Loading staffing data…</div>
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          {filteredRows.length === 0 ? (
            <div className="ngt-empty">
              <div className="ngt-empty__icon">✓</div>
              <div className="ngt-empty__text">
                {gymRows.length === 0
                  ? "All gyms are fully staffed — nothing to show here."
                  : "No gyms match the current filter."}
              </div>
              {activeFilter && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="ngt-toolbar__reload"
                    onClick={() => setActiveFilter(null)}
                  >
                    Clear filter
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="ngt-table-wrap">
              <table className="ngt-table" role="grid">
                <thead>
                  <tr>
                    <th className="ngt-th">Gym Name</th>
                    <th className="ngt-th">City</th>
                    <th className="ngt-th ngt-th--center">Added On</th>
                    <th className="ngt-th ngt-th--center">Days Since</th>
                    <th className="ngt-th ngt-th--center">
                      Trainers
                      <br />
                      <span style={{ fontWeight: 400, textTransform: "none", fontSize: "0.625rem" }}>
                        Have / Pending / Gap
                      </span>
                    </th>
                    <th className="ngt-th ngt-th--center">
                      Sales Execs
                      <br />
                      <span style={{ fontWeight: 400, textTransform: "none", fontSize: "0.625rem" }}>
                        Have / Pending / Gap
                      </span>
                    </th>
                    <th className="ngt-th ngt-th--center">Status</th>
                    <th className="ngt-th ngt-th--right">Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const isExpanded = expandedGymId === row.gymId;
                    const progressColor = getProgressColor(row.pct, row.status);

                    return (
                      <React.Fragment key={row.gymId}>
                        <tr
                          className={`ngt-row${isExpanded ? " ngt-row--active" : ""}`}
                          onClick={() => handleRowClick(row.gymId)}
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleRowClick(row.gymId)
                          }
                          title="Click to see staffing detail"
                        >
                          {/* Gym Name */}
                          <td className="ngt-td">
                            <span className="ngt-gym-name">{row.gymName}</span>
                          </td>

                          {/* City */}
                          <td className="ngt-td ngt-td--nowrap">
                            <span className="ngt-gym-city">{row.city || "—"}</span>
                          </td>

                          {/* Added On */}
                          <td className="ngt-td ngt-td--center ngt-td--nowrap">
                            <span className="ngt-date">{row.gymAddedDate}</span>
                          </td>

                          {/* Days Since Added */}
                          <td className="ngt-td ngt-td--center">
                            {row.daysSinceAdded != null ? (
                              <span className={`ngt-days-badge${row.daysSinceAdded > 30 ? " ngt-days-badge--warn" : row.daysSinceAdded > 90 ? " ngt-days-badge--danger" : ""}`}>
                                {row.daysSinceAdded}d
                              </span>
                            ) : "—"}
                          </td>

                          {/* Trainers */}
                          <td className="ngt-td ngt-td--center">
                            <div className="ngt-staffing-cell">
                              <span className="ngt-staffing-cell__have">
                                {row.approvedT}
                                <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                                  /{TRAINER_TARGET}
                                </span>
                              </span>
                              {row.pendingT > 0 && (
                                <span className="ngt-staffing-cell__pending">
                                  +{row.pendingT} pending
                                </span>
                              )}
                              <span
                                className={`ngt-staffing-cell__gap${row.trainerGap === 0 ? " ngt-staffing-cell__gap--none" : ""}`}
                              >
                                {row.trainerGap === 0 ? "No gap" : `${row.trainerGap} needed`}
                              </span>
                            </div>
                          </td>

                          {/* Sales Execs */}
                          <td className="ngt-td ngt-td--center">
                            <div className="ngt-staffing-cell">
                              <span className="ngt-staffing-cell__have">
                                {row.approvedR}
                                <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                                  /{RECEP_TARGET}
                                </span>
                              </span>
                              {row.pendingR > 0 && (
                                <span className="ngt-staffing-cell__pending">
                                  +{row.pendingR} pending
                                </span>
                              )}
                              <span
                                className={`ngt-staffing-cell__gap${row.recepGap === 0 ? " ngt-staffing-cell__gap--none" : ""}`}
                              >
                                {row.recepGap === 0 ? "No gap" : `${row.recepGap} needed`}
                              </span>
                            </div>
                          </td>

                          {/* Status badge */}
                          <td className="ngt-td ngt-td--center">
                            <span className={`ngt-badge ngt-badge--${row.status}`}>
                              {STATUS_LABELS[row.status]}
                            </span>
                          </td>

                          {/* Progress bar */}
                          <td className="ngt-td ngt-td--right">
                            <div className="ngt-progress">
                              <div className="ngt-progress__bar-track">
                                <div
                                  className={`ngt-progress__bar-fill ngt-progress__bar-fill--${progressColor}`}
                                  style={{ width: `${row.pct}%` }}
                                />
                              </div>
                              <div className="ngt-progress__pct">{row.pct}%</div>
                            </div>
                          </td>
                        </tr>

                        {/* Inline drill-down */}
                        {isExpanded && (
                          <tr className="ngt-drilldown-row">
                            <td colSpan={8}>
                              <div ref={drilldownRef}>
                                <GymDrillDown
                                  gym={row}
                                  onClose={() => setExpandedGymId(null)}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NewGymsTab;
